/**
 * CRS History Page - view score history over time for one entity.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Loader2,
  Briefcase,
  Building2,
  History as HistoryIcon,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import capitalReadinessService from '../services/capitalReadinessService';
import projectService from '../services/projectService';
import dataService, { CompanyRecord } from '../services/data';
import { CapitalReadinessScore } from '../types/capitalReadiness';
import CRSTierBadge from '../components/CRSTierBadge';
import { scoreColor } from './CRSDashboardPage';
import { Project } from '../types/project';

type EntityKind = 'project' | 'startup';

interface EntityOption {
  kind: EntityKind;
  id: number;
  label: string;
}

const formatDate = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString() : '—';
const formatDateTime = (s?: string | null) =>
  s ? new Date(s).toLocaleString() : '—';

const CRSHistoryPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const qsStartup = searchParams.get('startup_id');
  const qsProject = searchParams.get('project_id');

  const [options, setOptions] = useState<EntityOption[]>([]);
  const [loadingEntities, setLoadingEntities] = useState(true);
  const [kind, setKind] = useState<EntityKind>(
    qsProject ? 'project' : qsStartup ? 'startup' : 'project'
  );
  const [selectedId, setSelectedId] = useState<number | ''>(
    qsProject ? Number(qsProject) : qsStartup ? Number(qsStartup) : ''
  );
  const [history, setHistory] = useState<CapitalReadinessScore[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoadingEntities(true);
      const [projRes, compRes] = await Promise.all([
        projectService.fetchProjects({ size: 200 } as any),
        dataService.fetchCompanies(),
      ]);
      const opts: EntityOption[] = [];
      if (projRes.data?.projects) {
        projRes.data.projects.forEach((p: Project) =>
          opts.push({
            kind: 'project',
            id: p.id,
            label: p.project_name,
          })
        );
      }
      if (compRes.data) {
        compRes.data.forEach((c: CompanyRecord) =>
          opts.push({ kind: 'startup', id: c.id, label: c.name })
        );
      }
      setOptions(opts);
      setLoadingEntities(false);
    };
    load();
  }, []);

  const loadHistory = async () => {
    if (!selectedId) {
      setHistory([]);
      return;
    }
    setLoadingHistory(true);
    const res = await capitalReadinessService.fetchHistory(
      kind === 'project'
        ? { project_id: Number(selectedId) }
        : { startup_id: Number(selectedId) }
    );
    if (res.error) {
      toast.error(res.error);
      setHistory([]);
    } else {
      const sorted = [...(res.data || [])].sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
      setHistory(sorted);
    }
    setLoadingHistory(false);
  };

  useEffect(() => {
    loadHistory();
    // Sync URL params
    if (selectedId) {
      const sp = new URLSearchParams();
      sp.set(kind === 'project' ? 'project_id' : 'startup_id', String(selectedId));
      setSearchParams(sp, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, kind]);

  const filtered = options.filter((o) => o.kind === kind);

  // SVG chart dimensions
  const chartWidth = 720;
  const chartHeight = 240;
  const padding = { top: 20, right: 20, bottom: 40, left: 40 };

  const chartData = useMemo(() => {
    if (history.length === 0) return null;
    const innerW = chartWidth - padding.left - padding.right;
    const innerH = chartHeight - padding.top - padding.bottom;
    const n = history.length;
    const minT = new Date(history[0].created_at).getTime();
    const maxT = new Date(history[n - 1].created_at).getTime();
    const dt = maxT - minT || 1;

    const points = history.map((h, i) => {
      const x =
        n === 1
          ? padding.left + innerW / 2
          : padding.left +
            ((new Date(h.created_at).getTime() - minT) / dt) * innerW;
      const y = padding.top + innerH - (h.overall_score / 100) * innerH;
      return { x, y, score: h.overall_score, h };
    });
    const pathD = points
      .map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`))
      .join(' ');
    return { points, pathD, innerW, innerH };
  }, [history]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <Link
          to="/capital-readiness"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to capital readiness
        </Link>

        <div>
          <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center">
            <HistoryIcon className="w-6 h-6 mr-2 text-blue-600" />
            Score history
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Track readiness improvement over time.
          </p>
        </div>

        {/* Entity picker */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col md:flex-row gap-3 items-stretch md:items-end">
          <div className="flex-1">
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
              Entity type
            </label>
            <div className="flex gap-2">
              {(['project', 'startup'] as EntityKind[]).map((k) => (
                <button
                  key={k}
                  onClick={() => {
                    setKind(k);
                    setSelectedId('');
                  }}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg border text-sm capitalize ${
                    kind === k
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {k === 'project' ? (
                    <Briefcase className="w-4 h-4 mr-1.5" />
                  ) : (
                    <Building2 className="w-4 h-4 mr-1.5" />
                  )}
                  {k}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1">
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
              Select entity
            </label>
            {loadingEntities ? (
              <div className="flex items-center text-sm text-gray-600">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </div>
            ) : (
              <select
                value={selectedId}
                onChange={(e) =>
                  setSelectedId(
                    e.target.value === '' ? '' : Number(e.target.value)
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Choose...</option>
                {filtered.map((o) => (
                  <option key={`${o.kind}-${o.id}`} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Chart */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">
            Score trend
          </h3>
          {!selectedId ? (
            <p className="text-sm text-gray-500">Select an entity to view history.</p>
          ) : loadingHistory ? (
            <div className="flex items-center text-sm text-gray-600">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Loading...
            </div>
          ) : history.length === 0 ? (
            <p className="text-sm text-gray-500">No history yet.</p>
          ) : (
            <svg
              viewBox={`0 0 ${chartWidth} ${chartHeight}`}
              width="100%"
              height={chartHeight}
              preserveAspectRatio="xMidYMid meet"
            >
              {/* Y axis grid */}
              {[0, 25, 50, 75, 100].map((v) => {
                const y =
                  padding.top +
                  (chartHeight - padding.top - padding.bottom) * (1 - v / 100);
                return (
                  <g key={v}>
                    <line
                      x1={padding.left}
                      y1={y}
                      x2={chartWidth - padding.right}
                      y2={y}
                      stroke="#E5E7EB"
                      strokeWidth={1}
                    />
                    <text
                      x={padding.left - 8}
                      y={y}
                      textAnchor="end"
                      dominantBaseline="middle"
                      className="fill-gray-500"
                      style={{ fontSize: 10 }}
                    >
                      {v}
                    </text>
                  </g>
                );
              })}
              {/* Line */}
              {chartData && (
                <>
                  <path
                    d={chartData.pathD}
                    fill="none"
                    stroke="#2563EB"
                    strokeWidth={2}
                  />
                  {chartData.points.map((p, i) => (
                    <g key={i}>
                      <circle cx={p.x} cy={p.y} r={4} fill="#2563EB" />
                      <title>
                        {formatDateTime(p.h.created_at)} — Score {p.score}
                      </title>
                    </g>
                  ))}
                </>
              )}
            </svg>
          )}
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">Score records</h3>
          </div>
          {history.length === 0 ? (
            <div className="p-6 text-center text-gray-500 text-sm">
              No records.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">
                    Date
                  </th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700">
                    Score
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">
                    Tier
                  </th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700">
                    Gaps
                  </th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700">
                    Version
                  </th>
                  <th className="text-right px-3 py-2 font-medium text-gray-700">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[...history]
                  .sort(
                    (a, b) =>
                      new Date(b.created_at).getTime() -
                      new Date(a.created_at).getTime()
                  )
                  .map((s) => (
                    <tr key={s.id}>
                      <td className="px-3 py-2 text-gray-700">
                        {formatDate(s.created_at)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span
                          className={`text-base font-bold ${scoreColor(
                            s.overall_score
                          )}`}
                        >
                          {Math.round(s.overall_score)}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <CRSTierBadge tier={s.tier} size="sm" />
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700">
                        {(s.gaps || []).length}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-500">
                        {s.version ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Link
                          to={`/capital-readiness/scores/${s.id}`}
                          className="text-blue-600 hover:underline text-xs"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CRSHistoryPage;
